/**
 * ApCashFlowTab — Flujo de caja proyectado (solo egresos).
 */
import { useState } from 'react'
import { useApCashFlow } from '../hooks/useFinance'
import { TrendingDown } from 'lucide-react'
import { useTenantLocale, formatNumber } from '@/lib/locale'
import { useLocalizationConfig } from '@/modules/config/hooks/useConfig'
import { DataTable } from '@/components/ds/DataTable'

export default function ApCashFlowTab() {
  const [weeks, setWeeks] = useState(12)
  const { data, isLoading } = useApCashFlow(weeks)
  const { data: locConfig } = useLocalizationConfig()
  const locale = useTenantLocale()
  const currency = locConfig?.currency || 'CLP'
  const isCLP = currency === 'CLP'
  const fmt = (value: string | number) => {
    const n = typeof value === 'string' ? parseFloat(value) : value
    const decimals = isCLP ? 0 : (n % 1 !== 0 ? 2 : 0)
    return `$${formatNumber(n, locale, decimals)}`
  }

  if (isLoading) return <p className="text-center text-sm text-content-subtle py-8">Cargando...</p>
  if (!data) return null

  return (
    <>
      {/* Filters + currency */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <label htmlFor="ap-cashflow-weeks" className="text-xs font-medium text-content-muted">Horizonte</label>
          <select
            id="ap-cashflow-weeks"
            value={weeks}
            onChange={e => setWeeks(Number(e.target.value))}
            className="px-2.5 py-1.5 text-xs border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200"
          >
            <option value={4}>4 semanas</option>
            <option value={8}>8 semanas</option>
            <option value={12}>12 semanas</option>
            <option value={24}>24 semanas</option>
            <option value={52}>52 semanas</option>
          </select>
        </div>
        <p className="text-xs text-content-muted ml-auto">
          Montos en <span className="font-mono font-semibold text-content">{currency}</span>
          {locConfig?.currency_display && <span> ({locConfig.currency_display})</span>}
        </p>
      </div>

      <div className="mb-6 p-4 bg-danger-surface border border-danger-border rounded-xl">
        <div className="flex items-center gap-2 text-danger-content-soft text-xs font-medium mb-1">
          <TrendingDown size={14} /> Egresos totales proyectados
        </div>
        <p className="text-2xl font-bold text-danger-content">{fmt(data.total_outflow)}</p>
      </div>

      <DataTable
        columns={[
          { key: 'semana', header: 'Semana', render: (w: any) => `${w.week_start} — ${w.week_end}` },
          {
            key: 'outflow', header: 'Egresos', numeric: true,
            render: (w: any) => (
              <span className="text-danger-content-soft font-medium">
                {parseFloat(w.outflow) > 0 ? fmt(w.outflow) : '—'}
              </span>
            ),
          },
        ]}
        rows={data.weeks || []}
        rowKey={(_: any, i: number) => i}
        minWidth={420}
        caption="Proyección semanal de flujo por pagar"
      />
    </>
  )
}
